import { useMemo, useState, CSSProperties, RefObject } from "react";
import { HiX } from "react-icons/hi";
import { Box } from "rebass/styled-components";
import styled from "styled-components";
import { isMobileOnly } from "react-device-detect";
import { Link } from "react-router-dom";

import { useDisplayAd } from "MVVM/Hooks/useDisplayAd";
import { useUser } from "MVVM/Hooks/useUser";
import { useAds } from "MVVM/Providers";
import { SlotSize, SizeMapping } from "MVVM/Utilities/Ads/DisplayAdManager";

export type AdUnitType =
  | "banner"
  | "billboard"
  | "large-leaderboard"
  | "large-rectangle"
  | "large-skyscraper"
  | "medium-rectangle"
  | "mobile-leaderboard"
  | "skyscraper"
  | "wide-skyscraper"
  | "small-square"
  | "video_slate_single"
  | "lower_video_player_banner"
  | "player_top_left_bug";

interface ContainerBreakpoint {
  minWidth: number;
  sizes: SlotSize[];
  primarySize: SlotSize;
}

interface AdSizeConfig {
  sizes: SlotSize[];
  sizeMapping?: SizeMapping;
  primarySize: SlotSize;
  // Container-based breakpoints (used when containerWidth is provided)
  containerBreakpoints?: ContainerBreakpoint[];
}

// HLTV partners that should use restricted banner sizes
const UGG_PARTNERS = ["UDOTGG", "U.GG"];

const AD_SIZES: Record<AdUnitType, AdSizeConfig> = {
  banner: {
    sizes: [
      [728, 90],
      [468, 60],
      [320, 50],
      [320, 100],
    ],
    sizeMapping: [
      {
        viewport: [1024, 0],
        sizes: [
          [728, 90],
          [468, 60],
        ],
      },
      {
        viewport: [0, 0],
        sizes: [
          [320, 50],
          [320, 100],
        ],
      },
    ],
    primarySize: [728, 90],
    // Container breakpoints optimized for player width (not viewport)
    containerBreakpoints: [
      {
        minWidth: 750,
        sizes: [
          [728, 90],
          [468, 60],
        ],
        primarySize: [728, 90],
      },
      {
        minWidth: 480,
        sizes: [
          [468, 60],
          [320, 100],
        ],
        primarySize: [468, 60],
      },
      {
        minWidth: 0,
        sizes: [
          [320, 50],
          [320, 100],
        ],
        primarySize: [320, 100],
      },
    ],
  },
  billboard: {
    sizes: [[970, 250]],
    primarySize: [970, 250],
  },
  "large-leaderboard": {
    sizes: [
      [970, 90],
      [728, 90],
    ],
    sizeMapping: [
      {
        viewport: [1200, 0],
        sizes: [
          [970, 90],
          [728, 90],
        ],
      },
      {
        viewport: [0, 0],
        sizes: [[728, 90]],
      },
    ],
    primarySize: [970, 90],
  },
  "large-rectangle": {
    sizes: [[336, 280]],
    primarySize: [336, 280],
  },
  "large-skyscraper": {
    sizes: [[300, 600]],
    primarySize: [300, 600],
  },
  "medium-rectangle": {
    sizes: [[300, 250]],
    primarySize: [300, 250],
  },
  "mobile-leaderboard": {
    sizes: [
      [320, 50],
      [320, 100],
    ],
    primarySize: [320, 100],
    // Container breakpoints optimized for player width (not viewport)
    containerBreakpoints: [
      {
        minWidth: 0,
        sizes: [
          [320, 50],
          [320, 100],
        ],
        primarySize: [320, 100],
      },
    ],
  },
  skyscraper: {
    sizes: [[120, 600]],
    primarySize: [120, 600],
  },
  "wide-skyscraper": {
    sizes: [[300, 600]],
    primarySize: [300, 600],
  },
  "small-square": {
    sizes: [[200, 200]],
    primarySize: [200, 200],
  },
  video_slate_single: {
    sizes: [
      [608, 342],
      [300, 250],
      [250, 141],
    ],
    sizeMapping: [
      {
        viewport: [1500, 0],
        sizes: [
          [608, 342],
          [300, 250],
        ],
      },
      {
        viewport: [0, 0],
        sizes: [
          [300, 250],
          [250, 141],
        ],
      },
    ],
    primarySize: [608, 342],
    // Container breakpoints optimized for player width (not viewport)
    containerBreakpoints: [
      {
        minWidth: 650,
        sizes: [
          [608, 342],
          [300, 250],
        ],
        primarySize: [608, 342],
      },
      {
        minWidth: 320,
        sizes: [
          [300, 250],
          [250, 141],
        ],
        primarySize: [300, 250],
      },
      { minWidth: 0, sizes: [[250, 141]], primarySize: [250, 141] },
    ],
  },
  lower_video_player_banner: {
    sizes: [[468, 60]],
    primarySize: [468, 60],
    // Container breakpoints optimized for player width (not viewport)
    containerBreakpoints: [
      {
        minWidth: 480,
        sizes: [[468, 60]],
        primarySize: [468, 60],
      },
      {
        minWidth: 0,
        sizes: [
          [320, 50],
          [320, 100],
        ],
        primarySize: [320, 50],
      },
    ],
  },
  player_top_left_bug: {
    sizes: [
      [120, 60],
      [120, 40],
    ],
    primarySize: [120, 60],
  },
};

// Map adUnitType to the config key in AdsProvider
type AdUnitConfigKey =
  | "banner"
  | "billboard"
  | "largeLeaderboard"
  | "largeSkyscraper"
  | "mediumRectangle"
  | "mobileLeaderboard"
  | "videoSlateSingle"
  | "playerTopLeftBug"
  | "lower_video_player_banner";

const adUnitTypeToConfigKey: Record<AdUnitType, AdUnitConfigKey | null> = {
  banner: "banner",
  billboard: "billboard",
  "large-leaderboard": "largeLeaderboard",
  "large-rectangle": null,
  "large-skyscraper": "largeSkyscraper",
  "medium-rectangle": "mediumRectangle",
  "mobile-leaderboard": "mobileLeaderboard",
  skyscraper: null,
  "wide-skyscraper": null,
  "small-square": null,
  video_slate_single: "videoSlateSingle",
  lower_video_player_banner: "lower_video_player_banner",
  player_top_left_bug: "playerTopLeftBug",
};

interface AdUnitProps {
  slotId: string;
  adUnitType: AdUnitType;
  game?: string;
  onAdLoaded?: (hasAd: boolean, size?: number[] | string) => void;
  collapseIfEmpty?: boolean;
  styles?: CSSProperties;
  debug?: boolean;
  rounded?: boolean;
  /** Ref to the container element to measure for responsive sizing */
  containerRef?: RefObject<HTMLElement>;
  /** Allow user to dismiss the ad */
  dismissible?: boolean;
  /** Callback when ad is dismissed */
  onDismiss?: () => void;
  /** Show a "Subscribe to remove ads" CTA below the ad */
  showUpgradeCta?: boolean;
  /** Allow programmatic backfill for this ad unit */
  allowProgrammatic?: boolean;
}

// Helper to get sizes based on container width
const getSizesForContainer = (
  config: AdSizeConfig,
  containerWidth: number,
): { sizes: SlotSize[]; primarySize: SlotSize } => {
  if (!config.containerBreakpoints) {
    return { sizes: config.sizes, primarySize: config.primarySize };
  }

  // Find the first breakpoint where containerWidth >= minWidth
  for (const breakpoint of config.containerBreakpoints) {
    if (containerWidth >= breakpoint.minWidth) {
      return { sizes: breakpoint.sizes, primarySize: breakpoint.primarySize };
    }
  }

  // Fallback to last breakpoint (smallest)
  const lastBreakpoint =
    config.containerBreakpoints[config.containerBreakpoints.length - 1];
  return {
    sizes: lastBreakpoint.sizes,
    primarySize: lastBreakpoint.primarySize,
  };
};

const AdUnit = ({
  slotId,
  adUnitType,
  game,
  onAdLoaded,
  collapseIfEmpty = false,
  styles,
  debug = false,
  rounded = false,
  containerRef,
  dismissible = false,
  onDismiss,
  showUpgradeCta = false,
  allowProgrammatic = false,
}: AdUnitProps) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const { allstarUser } = useUser();
  const { config } = useAds();

  const userGroupType = allstarUser?.user?.userGroup?.type;
  const isAdFreeUser =
    userGroupType === "PRO" || userGroupType === "CREATOR_PLAT";

  const adSizeConfig = AD_SIZES[adUnitType];

  // Check if current partner is HLTV
  const platform = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("platform") ?? "";
  }, []);
  const isUggPartner = UGG_PARTNERS.includes(platform);

  // Get container width directly from the ref
  const containerWidth = containerRef?.current?.offsetWidth;

  // Get sizes based on container width or use default config
  // HLTV partners only get 320x50 banners
  const { sizes: effectiveSizes, primarySize: effectivePrimarySize } =
    useMemo(() => {
      // HLTV partner override - restricted ad sizes
      if (isUggPartner) {
        if (adUnitType === "banner" || adUnitType === "mobile-leaderboard") {
          return {
            sizes: [[320, 50]] as SlotSize[],
            primarySize: [320, 50] as SlotSize,
          };
        }
        if (adUnitType === "video_slate_single") {
          return {
            sizes: [
              [200, 200],
              [250, 141],
            ] as SlotSize[],
            primarySize: [200, 200] as SlotSize,
          };
        }
      }

      if (containerWidth && adSizeConfig.containerBreakpoints) {
        return getSizesForContainer(adSizeConfig, containerWidth);
      }
      return {
        sizes: adSizeConfig.sizes,
        primarySize: adSizeConfig.primarySize,
      };
    }, [adSizeConfig, containerWidth, isUggPartner, adUnitType]);

  // Check if this ad unit type is enabled for the current partner
  const isAdUnitEnabled = useMemo(() => {
    if (!config.display.enabled) return false;

    const configKey = adUnitTypeToConfigKey[adUnitType];
    // If no config key mapping, it's not a partner-specific ad unit
    if (!configKey) return false;

    return config.display.adUnits[configKey] === true;
  }, [config.display, adUnitType]);

  const displayConfig = useMemo(() => {
    if (!config.display.enabled || !isAdUnitEnabled) return null;

    return {
      networkId: config.display.networkId,
      adUnit: adUnitType,
    };
  }, [adUnitType, config.display, isAdUnitEnabled]);

  // When using container-based sizing, don't use viewport sizeMapping
  const effectiveSizeMapping =
    containerRef !== undefined ? undefined : adSizeConfig.sizeMapping;

  const {
    containerRef: adContainerRef,
    hasAd,
    isResolved,
    renderedSize,
  } = useDisplayAd({
    slotId,
    sizes: effectiveSizes,
    sizeMapping: effectiveSizeMapping,
    displayConfig,
    game,
    enabled: isAdUnitEnabled,
    onAdLoaded,
    allowProgrammatic,
  });

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  // Hide banner ads on mobile for HLTV partners
  const shouldHideForHLTVMobile =
    isUggPartner &&
    isMobileOnly &&
    (adUnitType === "banner" || adUnitType === "mobile-leaderboard");

  if (
    isAdFreeUser ||
    !isAdUnitEnabled ||
    isDismissed ||
    shouldHideForHLTVMobile
  ) {
    return null;
  }

  const shouldCollapse = collapseIfEmpty && isResolved && !hasAd;

  // Use rendered size if available, otherwise fall back to primary size
  // This allows the container to shrink to fit the actual ad
  const displaySize: SlotSize =
    renderedSize && Array.isArray(renderedSize) && renderedSize.length === 2
      ? (renderedSize as SlotSize)
      : effectivePrimarySize;

  return (
    <AdWrapper $collapsed={shouldCollapse} style={styles}>
      {dismissible && hasAd && (
        <DismissButton onClick={handleDismiss} aria-label="Dismiss ad">
          <HiX size={12} />
        </DismissButton>
      )}
      <AdContainer
        ref={adContainerRef}
        id={slotId}
        $width={displaySize[0]}
        $height={displaySize[1]}
        $hasAd={hasAd}
        $collapsed={shouldCollapse}
        $debug={debug}
        $rounded={rounded}
      />
      {showUpgradeCta && hasAd && (
        <UpgradeCta to="/upgrade">Remove Ads with Pro</UpgradeCta>
      )}
    </AdWrapper>
  );
};

const AdWrapper = styled(Box)<{ $collapsed: boolean }>`
  position: relative;
  display: ${({ $collapsed }) => ($collapsed ? "none" : "block")};
`;

const DismissButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  z-index: 10;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const AdContainer = styled(Box)<{
  $width: number;
  $height: number;
  $hasAd: boolean;
  $collapsed: boolean;
  $debug?: boolean;
  $rounded?: boolean;
}>`
  width: ${({ $width, $collapsed }) => ($collapsed ? 0 : $width)}px;
  height: ${({ $height, $collapsed }) => ($collapsed ? 0 : $height)}px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  transition: height 0.3s ease-out;
  ${({ $rounded }) =>
    $rounded &&
    `
    border-radius: 12px;
    overflow: hidden;
    
    iframe {
      border-radius: 12px;
    }
    
    div {
      border-radius: 12px;
      overflow: hidden;
    }
  `}
  ${({ $debug }) => $debug && "border: 2px solid blue;"}
`;

const UpgradeCta = styled(Link)`
  display: block;
  text-align: center;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  color: ${({ theme }) => theme.colors.ice};
  background: ${({ theme }) => theme.colors.premiumPurpleAlpha15};
  border: 1px solid ${({ theme }) => theme.colors.premiumPurpleAlpha40};
  border-radius: 999px;
  text-decoration: none;
  max-width: 150px;
  margin: 0 auto;
  margin-top: 6px;
  box-shadow: 0 4px 14px ${({ theme }) => theme.colors.premiumPurpleAlpha30};
  transition:
    color 0.2s ease,
    background 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.premiumPurpleAlpha30};
    transform: translateY(-1px);
    box-shadow: 0 6px 18px ${({ theme }) => theme.colors.premiumPurpleAlpha50};
  }

  &:active {
    transform: translateY(0);
  }
`;

export { AdUnit };
